import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import styles from '@/styles/Categories.module.css';
import { getProductInfo, selectItem, selectPromotion, bannersAnalyticsFunction } from '@/utils/analytics.js';
import React, { useRef, useEffect, useState, useCallback, useContext } from 'react';
import { ProductCatalogueV2, SnackBar, OffersSnackbar } from '@desquared/wave-components-library';
import { groupProductsByCategory } from '@/utils/category';
import {
  addProduct,
  addQuantityLimit,
  removeProduct,
  getBundleOfferDescription,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
} from '@/utils/products';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { openListSideSheet } from '@/utils/products';
import { checkListStatus } from '@/utils/lists';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage.js';

export default function CategoryCatalogue({
  products,
  handleLoadMore,
  isLoading,
  isFetchingNextPage,
  limitations,
  side_banner,
  bundleOffersSummary,
  currentCategory,
  hasSubCategories,
  side_banner_link,
}) {
  const observer = useRef(null);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const { formatter } = useContext(CurrencyContext);
  const t = useTranslations('common');
  const t1 = useTranslations('offers');
  const router = useRouter();
  const defaultProductImage = useGetDefaultProductImage();
  const offersCatalogueWordings = {
    noItemsFoundWording: t1('empty.no_items_found'),
    noItemsFoundDescription: t1('empty.no_items_found_description'),
  };

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, handleLoadMore]
  );

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);

    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const groupedProducts = groupProductsByCategory(products, hasSubCategories ? null : currentCategory);
  addQuantityLimit(groupedProducts, setShowSnackbar, limitations);

  useEffect(() => {
    if (side_banner)
      bannersAnalyticsFunction(
        {
          altText: currentCategory ? `side banner ${currentCategory.name}` : 'side banner',
        },
        'view'
      );
  }, [currentCategory]);

  return (
    <>
      {isLoading ? (
        <ProductCatalogueV2 products={new Array(1)} isLoading loaderTilesAmount={15} />
      ) : groupedProducts?.length > 0 ? (
        groupedProducts.map((i, index) => (
          <div key={i.category + i.name} className={styles.catalogueRow}>
            <ProductCatalogueV2
              redirectToProduct={
                side_banner_link
                  ? () => {
                      router.push(`${side_banner_link}`);
                      selectPromotion({
                        creative_name: `side banner ${currentCategory.name}`,
                        creative_slot: `side banner ${currentCategory.name}`,
                        promotion_id: `side banner ${currentCategory.name}`,
                        promotion_name: `side banner ${currentCategory.name}`,
                      });
                    }
                  : null
              }
              title={i.category}
              products={i.products.map(p => ({
                ...p,
                qsUnavailableText: p.quantityLimit === 0 ? t('asides.out_of_stock') : t('qs.default_unavailable'),
              }))}
              onListClick={openListSideSheet}
              checkStatus={checkListStatus}
              addProduct={product => {
                addProduct(
                  product,
                  false,
                  {
                    item_list_id: 'category',
                    item_list_name: i.category,
                  },
                  false,
                  t('asides.side_cart.no_comment')
                );
              }}
              removeProduct={product =>
                removeProduct(
                  product,
                  {
                    item_list_id: 'category',
                    item_list_name: i.category,
                  },
                  t('asides.side_cart.no_comment')
                )
              }
              image={index === 0 && side_banner ? side_banner : undefined}
              LinkComponent={props => {
                const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                return (
                  <Link
                    prefetch={false}
                    {...propsWithCleanUrl}
                    onClick={() => {
                      const product = getProductBasedOnSlug(i.products, propsWithCleanUrl.href);

                      if (product) {
                        selectItem(getProductInfo(product.originalValues), {
                          item_list_id: 'category',
                          item_list_name: i.category,
                          index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                        });
                      }
                    }}
                  />
                );
              }}
              isLoading={isLoading}
              defaultQsText={t('qs.default')}
              defaultQsUnavailableText={t('qs.default_unavailable')}
              lastItemRef={index === groupedProducts.length - 1 ? lastResultItemRef : undefined}
              ImageComponent={CustomImage}
              offerLabelHasRibbon={process.env.offerLabelHasRibbon}
              isTileDetailsAnimated={process.env.isTileDetailsAnimated}
              ctaIcon={process.env.ctaIcon}
              listIcon={process.env.listIcon}
              noItemsFoundWording={offersCatalogueWordings.noItemsFoundWording}
              noItemsFoundDescription={offersCatalogueWordings.noItemsFoundDescription}
              currencyFormatter={formatter}
              hasRoundedCorners={process.env.hasRoundedCorners}
              defaultProductImage={defaultProductImage}
              forYouLabel={t('user.profile.menu.for_you')}
            />
          </div>
        ))
      ) : (
        <ProductCatalogueV2
          title={''}
          products={[]}
          isLoading={isLoading}
          defaultQsText={t('qs.default')}
          defaultQsUnavailableText={t('qs.default_unavailable')}
          ImageComponent={CustomImage}
          noItemsFoundWording={offersCatalogueWordings.noItemsFoundWording}
          noItemsFoundDescription={offersCatalogueWordings.noItemsFoundDescription}
          currencyFormatter={formatter}
          hasRoundedCorners={process.env.hasRoundedCorners}
        />
      )}
      {isFetchingNextPage ? <ProductCatalogueV2 products={new Array(1)} isLoading loaderTilesAmount={5} /> : null}
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('snackbar')} position={'center'} />
      {showBundleOffersSnackBar && (
        <OffersSnackbar
          emoji="🎉"
          open={showBundleOffersSnackBar}
          setOpen={BundleOfferSnackBarOpen.update}
          hasCloseOption={true}
          position="center"
          type="warning"
          autoWidth={true}
          buttonText={t('offers_snack_bar.btn_label')}
          text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
          onButtonPress={() => {
            setShowBundleOffersSnackBar(false);
            BundleOfferSnackBarOpen.update(false);
            BundleOfferSideSheetStore.update(true);
          }}
          autoHideDuration={8000}
          closeOnClickAway={true}
          hasShadow={true}
        />
      )}
    </>
  );
}
